import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Sync,
  Search,
  Download,
  Refresh,
  CheckCircle,
  HourglassEmpty,
  Error as ErrorIcon,
  Info,
  FolderZip,
  CloudDownload,
  Delete,
  Visibility,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, differenceInDays, addDays } from 'date-fns';
import { api } from '@/services/api';
import toast from 'react-hot-toast';

const TIPOS_MOVIMENTO = [
  { value: 1, label: 'Entrada', description: 'Boletos incluídos na carteira' },
  { value: 2, label: 'Prorrogação', description: 'Boletos com vencimento prorrogado' },
  { value: 3, label: 'A Vencer', description: 'Boletos com vencimento próximo' },
  { value: 4, label: 'Vencido', description: 'Boletos vencidos e não pagos' },
  { value: 5, label: 'Liquidação', description: 'Boletos pagos/liquidados' },
  { value: 6, label: 'Baixa', description: 'Boletos baixados/cancelados' },
  { value: 7, label: 'Vinculado', description: 'Operações de crédito vinculadas' },
];

interface SolicitacaoStatus {
  id?: string;
  codigoSolicitacao: number;
  quantidadeTotalRegistros?: string;
  quantidadeRegistrosArquivo?: number;
  quantidadeArquivo?: number;
  idArquivos?: number[];
  status: 'pending' | 'processing' | 'ready' | 'error' | 'downloaded' | 'empty';
  tipoMovimento: number;
  dataInicial: string;
  dataFinal: string;
  createdAt?: string;
  errorMessage?: string;
}

const BoletoMovimentacoesPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Form states
  const [tipoMovimento, setTipoMovimento] = useState<number>(5);
  const [dataInicial, setDataInicial] = useState<Date | null>(new Date());
  const [dataFinal, setDataFinal] = useState<Date | null>(new Date());

  // Action states
  const [loading, setLoading] = useState(false);
  const [loadingHistorico, setLoadingHistorico] = useState(true);
  const [consultaLoading, setConsultaLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [autoChecking, setAutoChecking] = useState(false);
  const [autoCheckDone, setAutoCheckDone] = useState(false);

  // Solicitações state
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoStatus[]>([]);
  const [codigoConsulta, setCodigoConsulta] = useState('');

  // Load solicitações from API on mount
  React.useEffect(() => {
    loadHistorico();
  }, []);

  // Auto-check pending/processing requests
  const checkPendingRequests = async (requests: SolicitacaoStatus[]) => {
    const pendingRequests = requests.filter(
      (sol) => sol.status === 'pending' || sol.status === 'processing'
    );

    if (pendingRequests.length === 0) {
      setAutoCheckDone(true);
      return;
    }

    setAutoChecking(true);

    // Check each pending request with a small delay between them
    for (const sol of pendingRequests) {
      try {
        await api.get('/boletos/movimentacoes', {
          params: { codigoSolicitacao: sol.codigoSolicitacao },
        });
        // Small delay between requests to avoid rate limiting
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (err) {
        // Ignore errors during auto-check
        console.log(`Auto-check for ${sol.codigoSolicitacao} failed:`, err);
      }
    }

    setAutoChecking(false);
    setAutoCheckDone(true);

    // Reload historico to show updated statuses
    const response = await api.get('/boletos/movimentacoes/historico');
    if (response?.success && response.data) {
      setSolicitacoes(response.data);
    }
  };

  // Check pending requests after initial load (only once)
  React.useEffect(() => {
    if (!loadingHistorico && solicitacoes.length > 0 && !autoCheckDone && !autoChecking) {
      checkPendingRequests(solicitacoes);
    }
  }, [loadingHistorico, autoCheckDone, autoChecking]);

  const loadHistorico = async () => {
    setLoadingHistorico(true);
    try {
      const response = await api.get('/boletos/movimentacoes/historico');
      if (response?.success && response.data) {
        setSolicitacoes(response.data);
      }
    } catch (err: any) {
      console.error('Error loading historico:', err);
    } finally {
      setLoadingHistorico(false);
    }
  };

  // Validate date range (max 2 days)
  const validateDateRange = (): boolean => {
    if (!dataInicial || !dataFinal) {
      setError('Informe as datas inicial e final');
      return false;
    }

    const diff = differenceInDays(dataFinal, dataInicial);
    if (diff < 0) {
      setError('A data final deve ser maior ou igual à data inicial');
      return false;
    }
    if (diff > 2) {
      setError('O período máximo permitido é de 2 dias');
      return false;
    }

    return true;
  };

  // Auto-adjust dates to maintain max 2 days range
  const handleDataInicialChange = (date: Date | null) => {
    setDataInicial(date);
    if (date && dataFinal) {
      const diff = differenceInDays(dataFinal, date);
      if (diff > 2) {
        setDataFinal(addDays(date, 2));
      } else if (diff < 0) {
        setDataFinal(date);
      }
    }
  };

  const handleDataFinalChange = (date: Date | null) => {
    setDataFinal(date);
    if (date && dataInicial) {
      const diff = differenceInDays(date, dataInicial);
      if (diff > 2) {
        setDataInicial(addDays(date, -2));
      } else if (diff < 0) {
        setDataInicial(date);
      }
    }
  };

  // Request new movement
  const handleSolicitarMovimentacao = async () => {
    if (!validateDateRange()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/boletos/movimentacoes', {
        tipoMovimento,
        dataInicial: format(dataInicial!, 'yyyy-MM-dd'),
        dataFinal: format(dataFinal!, 'yyyy-MM-dd'),
      });

      if (response?.success && response.data?.resultado) {
        const { codigoSolicitacao, mensagem } = response.data.resultado;

        toast.success(mensagem || 'Solicitação enviada com sucesso!');
        setCodigoConsulta(String(codigoSolicitacao));

        // Reload list from database
        await loadHistorico();
      } else {
        setError(response?.error?.message || 'Erro ao solicitar movimentação');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao solicitar movimentação');
    } finally {
      setLoading(false);
    }
  };

  // Check status of a request
  const handleConsultarStatus = async (codigo?: number) => {
    const codigoToConsult = codigo || parseInt(codigoConsulta);

    if (!codigoToConsult) {
      setError('Informe o código da solicitação');
      return;
    }

    setConsultaLoading(true);
    setError(null);

    try {
      const response = await api.get('/boletos/movimentacoes', {
        params: { codigoSolicitacao: codigoToConsult },
      });

      if (response?.success && response.data?.resultado) {
        const resultado = response.data.resultado;

        if (resultado.idArquivos && resultado.idArquivos.length > 0) {
          toast.success(`${resultado.quantidadeArquivo} arquivo(s) disponível(is) para download`);
        } else if (resultado.quantidadeTotalRegistros === '0' || resultado.quantidadeTotalRegistros === 0) {
          // No records found for the period (processed successfully but empty)
          toast('Nenhum registro encontrado para o período solicitado.', { icon: 'ℹ️' });
        } else {
          toast('Solicitação ainda está sendo processada', { icon: '⏳' });
        }

        // Reload from database to get updated status
        await loadHistorico();
      } else {
        const errorMsg = response?.error?.message || '';
        // Handle specific Sicoob responses
        if (errorMsg.includes('ainda está sendo processada') || errorMsg.includes('em processamento')) {
          toast('Solicitação ainda está sendo processada. Tente novamente em alguns minutos.', { icon: '⏳' });
          await loadHistorico();
        } else {
          setError(errorMsg || 'Erro ao consultar status');
        }
      }
    } catch (err: any) {
      const errorMsg = err.message || '';
      if (err.status === 204 || errorMsg.includes('em processamento')) {
        toast('Solicitação ainda está sendo processada. Tente novamente em alguns minutos.', { icon: '⏳' });
        await loadHistorico();
      } else {
        setError(errorMsg || 'Erro ao consultar status');
      }
    } finally {
      setConsultaLoading(false);
    }
  };

  // Download a file
  const handleDownloadArquivo = async (codigoSolicitacao: number, idArquivo: number) => {
    setDownloadLoading(idArquivo);

    try {
      const response = await api.get('/boletos/movimentacoes/download', {
        params: { codigoSolicitacao, idArquivo },
      });

      if (response?.success && response.data?.resultado) {
        const { arquivo, nomeArquivo } = response.data.resultado;

        // Decode base64 and download
        const byteCharacters = atob(arquivo);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/zip' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = nomeArquivo || `movimentacao_${codigoSolicitacao}_${idArquivo}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success('Arquivo baixado com sucesso!');
      } else {
        toast.error(response?.error?.message || 'Erro ao baixar arquivo');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao baixar arquivo');
    } finally {
      setDownloadLoading(null);
    }
  };

  // Delete a solicitação
  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja remover esta solicitação do histórico?')) {
      return;
    }

    setDeletingId(id);
    try {
      const response = await api.delete(`/boletos/movimentacoes/${id}`);
      if (response?.success) {
        toast.success('Solicitação removida');
        await loadHistorico();
      } else {
        toast.error(response?.error?.message || 'Erro ao remover solicitação');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao remover solicitação');
    } finally {
      setDeletingId(null);
    }
  };

  const getTipoMovimentoLabel = (tipo: number) => {
    const found = TIPOS_MOVIMENTO.find(t => t.value === tipo);
    return found?.label || `Tipo ${tipo}`;
  };

  // Navigate to viewer page
  const handleViewArquivo = (codigoSolicitacao: number, idArquivo: number, tipo: number) => {
    navigate(`/boletos/movimentacoes/viewer?codigo=${codigoSolicitacao}&arquivo=${idArquivo}&tipo=${tipo}`);
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'ready':
        return <Chip icon={<CheckCircle />} label="Pronto" color="success" size="small" />;
      case 'pending':
        return <Chip icon={<HourglassEmpty />} label="Aguardando" color="default" size="small" />;
      case 'processing':
        return <Chip icon={<HourglassEmpty />} label="Processando" color="warning" size="small" />;
      case 'downloaded':
        return <Chip icon={<CheckCircle />} label="Baixado" color="info" size="small" />;
      case 'empty':
        return <Chip icon={<Info />} label="Sem registros" color="default" size="small" />;
      case 'error':
        return <Chip icon={<ErrorIcon />} label="Erro" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        <Sync sx={{ mr: 1, verticalAlign: 'middle' }} />
        Movimentação da Carteira
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Form de Solicitação */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Nova Solicitação
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Solicite a movimentação da carteira para download posterior.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Período Máximo</AlertTitle>
              As consultas estão limitadas a um período máximo de <strong>2 dias</strong>.
            </Alert>

            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Movimento</InputLabel>
                <Select
                  value={tipoMovimento}
                  label="Tipo de Movimento"
                  onChange={(e) => setTipoMovimento(e.target.value as number)}
                >
                  {TIPOS_MOVIMENTO.map((tipo) => (
                    <MenuItem key={tipo.value} value={tipo.value}>
                      <Box>
                        <Typography variant="body2">{tipo.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tipo.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DatePicker
                label="Data Inicial"
                value={dataInicial}
                onChange={handleDataInicialChange}
                slotProps={{ textField: { fullWidth: true } }}
              />

              <DatePicker
                label="Data Final"
                value={dataFinal}
                onChange={handleDataFinalChange}
                slotProps={{ textField: { fullWidth: true } }}
              />

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleSolicitarMovimentacao}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <CloudDownload />}
              >
                {loading ? 'Solicitando...' : 'Solicitar Movimentação'}
              </Button>
            </Stack>
          </Paper>
        </Grid>

        {/* Consulta de Status */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Consultar Status
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Verifique se sua solicitação já foi processada.
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Código da Solicitação"
                value={codigoConsulta}
                onChange={(e) => setCodigoConsulta(e.target.value)}
                placeholder="Ex: 132"
                type="number"
              />
              <Button
                variant="outlined"
                onClick={() => handleConsultarStatus()}
                disabled={consultaLoading || !codigoConsulta}
                startIcon={consultaLoading ? <CircularProgress size={20} /> : <Search />}
                sx={{ minWidth: 140 }}
              >
                Consultar
              </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" color="text.secondary">
                  Histórico de Solicitações
                </Typography>
                {autoChecking && (
                  <Chip
                    icon={<CircularProgress size={12} />}
                    label="Verificando pendentes..."
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
              </Stack>
              <Tooltip title="Atualizar lista">
                <IconButton size="small" onClick={loadHistorico} disabled={loadingHistorico || autoChecking}>
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {loadingHistorico ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={32} />
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  Carregando...
                </Typography>
              </Box>
            ) : solicitacoes.length === 0 ? (
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  backgroundColor: alpha(theme.palette.grey[500], 0.08),
                  borderRadius: 1,
                }}
              >
                <Info sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">
                  Nenhuma solicitação encontrada.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {solicitacoes.map((sol) => (
                  <Card key={sol.id || sol.codigoSolicitacao} variant="outlined">
                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          #{sol.codigoSolicitacao}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {getStatusChip(sol.status)}
                          {sol.id && (
                            <Tooltip title="Remover do histórico">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(sol.id!)}
                                disabled={deletingId === sol.id}
                              >
                                {deletingId === sol.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <Delete fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Stack>

                      <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                        <Chip
                          label={getTipoMovimentoLabel(sol.tipoMovimento)}
                          size="small"
                          variant="outlined"
                        />
                        {sol.dataInicial && (
                          <Typography variant="caption" color="text.secondary">
                            {sol.dataInicial} a {sol.dataFinal}
                          </Typography>
                        )}
                      </Stack>

                      {sol.status === 'empty' ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Nenhum registro encontrado para o período
                        </Typography>
                      ) : sol.quantidadeTotalRegistros && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {sol.quantidadeTotalRegistros} registros em {sol.quantidadeArquivo} arquivo(s)
                        </Typography>
                      )}

                      {sol.errorMessage && (
                        <Alert severity="error" sx={{ mb: 1, py: 0 }}>
                          {sol.errorMessage}
                        </Alert>
                      )}

                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Tooltip title="Atualizar status no Sicoob">
                          <IconButton
                            size="small"
                            onClick={() => handleConsultarStatus(sol.codigoSolicitacao)}
                            disabled={consultaLoading}
                          >
                            <Refresh fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {sol.idArquivos && sol.idArquivos.map((idArquivo) => (
                          <Stack key={idArquivo} direction="row" spacing={0.5}>
                            <Tooltip title="Visualizar dados">
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => handleViewArquivo(sol.codigoSolicitacao, idArquivo, sol.tipoMovimento)}
                                sx={{ minWidth: 'auto', px: 1 }}
                              >
                                <Visibility fontSize="small" />
                              </Button>
                            </Tooltip>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                downloadLoading === idArquivo ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <FolderZip />
                                )
                              }
                              onClick={() => handleDownloadArquivo(sol.codigoSolicitacao, idArquivo)}
                              disabled={downloadLoading === idArquivo}
                            >
                              Arquivo {idArquivo}
                            </Button>
                          </Stack>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Info sobre tipos de movimento */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Tipos de Movimentação
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Descrição</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {TIPOS_MOVIMENTO.map((tipo) => (
                <TableRow key={tipo.value} hover>
                  <TableCell>{tipo.value}</TableCell>
                  <TableCell>{tipo.label}</TableCell>
                  <TableCell>{tipo.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default BoletoMovimentacoesPage;
